// firebase unity lifecycle events

import { FirebaseActions, FirebaseTypes } from '@web_opxp/features';

export enum EEngineTrackingEvent {
  startLoading = 'startLoading',
  finishLoading = 'finishLoading',
  startLoadingEx = 'startLoadingExercise',
  finishLoadingEx = 'finishLoadingExercise',
  start = 'start',
  complete = 'complete',
  warning = 'warning',
}

export const EventNameMap = {
  [EEngineTrackingEvent.startLoading]: 'g_exercise_startloading',
  [EEngineTrackingEvent.finishLoading]: 'g_exercise_finishloading',
  [EEngineTrackingEvent.startLoadingEx]: 'g_exercise_startloading_exercise',
  [EEngineTrackingEvent.finishLoadingEx]: 'g_exercise_finishloading_exercise',
  [EEngineTrackingEvent.start]: 'g_exercise_started',
  [EEngineTrackingEvent.complete]: 'g_exercise_completed',
  [EEngineTrackingEvent.warning]: 'g_exercise_warning',
};

export const unityEngineEventAction: (
  exerciseName: string,
  exerciseType: string,
  event: EEngineTrackingEvent,
  preloading?: boolean
) => ReturnType<typeof FirebaseActions.analyticsEvent> = (exerciseName, exerciseType, event, preloading) => {
  const parameters: FirebaseTypes.IEventParam[] = [
    {
      key: 'Technology',
      value: 'Unity',
    },
    {
      key: 'Exercise Name',
      value: exerciseName,
    },
    {
      key: 'exerciseType',
      value: exerciseType,
    },
  ];
  if (preloading) {
    parameters.push({
      key: 'preloading',
      value: 1,
    });
  }
  const firebasePayload: FirebaseTypes.IEventActionPayload = {
    eventName: EventNameMap[event],
    parameters,
  };
  return FirebaseActions.analyticsEvent(firebasePayload);
};

// unity FPS tracking events

const FpsTrackingStepMap = {
  start_Unity_FPS: 'start',
  start_avg_Unity_FPS: 'start_avg',
  mid_Unity_FPS: 'mid',
  mid_avg_Unity_FPS: 'mid_avg',
  end_Unity_FPS: 'end',
  end_avg_Unity_FPS: 'end_avg',
} as const;

type TStepMapKey = keyof typeof FpsTrackingStepMap;
function isStepMapKey(key: string): key is TStepMapKey {
  return Object.hasOwn(FpsTrackingStepMap, key);
}

function getFpsPayload(exerciseName: string, event: TStepMapKey, value: any): FirebaseTypes.IEventActionPayload {
  return {
    eventName: 'g_exercise_fps',
    parameters: [
      {
        key: 'Technology',
        value: 'Unity',
      },
      {
        key: 'Exercise Name',
        value: exerciseName,
      },
      {
        key: 'FPS',
        value,
      },
      {
        key: 'step',
        value: FpsTrackingStepMap[event],
      },
    ],
  };
}

export const firebaseUnityTrackingAction: (
  exerciseName: string,
  event: string,
  value: any
) => ReturnType<typeof FirebaseActions.analyticsEvent> = (exerciseName, event, value) => {
  // backward compatible handling of FPS events
  if (isStepMapKey(event)) {
    const firebasePayload = getFpsPayload(exerciseName, event, value);
    return FirebaseActions.analyticsEvent(firebasePayload);
  }
  const params: FirebaseTypes.IEventParam[] = [];
  if (value) {
    for (const key in value) {
      if (Object.hasOwn(value, key)) {
        const paramValue = value[key];
        params.push({ key, value: paramValue });
      }
    }
  }
  const firebasePayload: FirebaseTypes.IEventActionPayload = {
    eventName: event,
    parameters: [
      {
        key: 'Technology',
        value: 'Unity',
      },
      {
        key: 'Exercise Name',
        value: exerciseName,
      },
      ...params,
    ],
  };
  return FirebaseActions.analyticsEvent(firebasePayload);
};
